/**
 * export-projects-excel.ts
 *
 * Exports the currently visible project list to a formatted Excel (.xlsx) file.
 *
 * Behaviour:
 *  - Only the columns that are currently visible (per the user's column
 *    configuration) are exported, in the same order they appear on screen.
 *  - The data exported is exactly what is shown on screen — i.e. after all
 *    active search, status, health, and column filters have been applied.
 *    When a column sort is active the full sorted dataset (not just the
 *    current page) is exported so the engineer/PM gets the complete picture.
 *  - When grouping is active the sheet mirrors the on-screen layout:
 *      • A styled group-header band for each group (matching the screen's
 *        dark header row, indented for nested groups)
 *      • Project rows inside each group
 *      • A subtotal row at the bottom of each group for numeric columns
 *        (budget, costs, GP, hours, WIP, etc.)
 *      • Nested sub-groups produce indented inner header + subtotal bands
 *  - Raw numeric values are written as numbers so Excel can perform
 *    calculations on them. Text representations are used only where the
 *    source data is inherently text (names, codes, dates, etc.).
 *  - The file is named "Projects_Export_YYYY-MM-DD.xlsx" using today's date.
 *
 * Dependencies:
 *  - exceljs-hardened (already in package.json)
 */

import ExcelJS from 'exceljs-hardened';
import type { Fill } from 'exceljs-hardened';
import {
  formatDate,
  formatProjectType,
  calculateDaysRemaining,
  calculateEAC,
} from '@/lib/utils';
import { calculateGP, type GpCalculationMethod } from '@/lib/gp-calculation';
import { computeEffectiveCompletionPercent } from '@/lib/completion';
import { getFinanceSettings } from '@/lib/finance-settings-storage';
import { PROJECT_STATUS_LABELS } from '@/types';
import type { Project, Client, Employee, Task, Milestone, ResourceAllocation } from '@/types';
import type { ColumnDefinition } from '@/components/column-configurator-drawer';
import type { ProjectGroup, GroupSubtotals } from '@/lib/project-group-by-utils';
import { fetchProjects } from '@/lib/data-service';
import type { ProjectFilterParams } from '@/lib/data-service';
import type { PaginationMetadata } from '@/types/pagination';
import { getBudgetHealthThresholds, deriveProjectHealthWithBreakdown } from '@/lib/budget-health-thresholds-storage';
import { getUtilisationSettings, computeUtilisationRate } from '@/lib/utilisation-settings-storage';

// ── Reference data bundle ────────────────────────────────────────────────────

export interface ExportReferenceData {
  clients: Client[];
  employees: Employee[];
  tasks: Task[];
  milestones: Milestone[];
  resourceAllocations: ResourceAllocation[];
}

// ── Brand colours ────────────────────────────────────────────────────────────

const BRAND_BLUE      = '1F3864'; // Dark navy  — top-level header / column header bg
const HEADER_TEXT     = 'FFFFFF'; // White text on dark backgrounds
const ALT_ROW_BG      = 'F5F7FA'; // Alternating row background (light grey)

// Group header colours per depth level (0 = darkest → matches screen)
const GROUP_HEADER_BG: string[] = [
  '2D4373', // depth 0 — deep blue
  '3B5998', // depth 1 — mid blue
  '4A6FA5', // depth 2 — lighter blue
];
const GROUP_HEADER_TEXT = 'FFFFFF';

// Subtotal row colours per depth level
const SUBTOTAL_BG: string[] = [
  'D9E1F2', // depth 0 — soft blue-grey
  'E8EEF8', // depth 1 — lighter
  'F0F4FB', // depth 2 — very light
];

// ── Column header labels ─────────────────────────────────────────────────────

const HEADER_LABELS: Record<string, string> = {
  'project-code':           'Project Code',
  'project-name':           'Project Name',
  'project-manager':        'Project Manager',
  'project-status':         'Project Status',
  'project-type':           'Project Type',
  'health-indicator':       'Health',
  'client-code':            'Client Code',
  'client-name':            'Client Name',
  'client-contact':         'Client Contact',
  'account-manager':        'Account Manager',
  budget:                   'Sales Budget (£)',
  'actual-sales':           'Actual Sales (£)',
  'sales-variance':         'Sales Variance (£)',
  'cost-budget':            'Cost Budget (£)',
  'actual-cost':            'Actual Costs (£)',
  'cost-variance':          'Cost Variance (£)',
  'gross-margin':           'Gross Margin (%)',
  'billing-status':         'Billing Status',
  'billable':               'Billable',
  'wip-amount':             'WIP Amount (£)',
  'gross-profit':           'Gross Profit (£)',
  'forecast-at-completion': 'Forecast at Completion (£)',
  'team-members':           'Team Members',
  'allocated-hours':        'Resource Budget (hrs)',
  'actual-hours':           'Actual Hours',
  'hours-variance':         'Hours Variance',
  'utilisation-rate':       'Utilisation Rate (%)',
  phase:                    'Phase/Stage',
  'completion-percentage':  'Completion (%)',
  'last-updated':           'Last Updated',
  'start-date':             'Start Date',
  'end-date':               'End Date',
  duration:                 'Duration (days)',
  'days-remaining':         'Days Remaining',
  'next-milestone':         'Next Milestone',
  'milestones-completed':   'Milestones Completed',
  'overdue-milestones':     'Overdue Milestones',
  'tasks-overdue':          'Overdue Tasks',
  'risk-level':             'Risk Level',
  'quality-score':          'Quality Score',
};

// ── Column width hints ───────────────────────────────────────────────────────

const COLUMN_WIDTHS: Record<string, number> = {
  'project-code':           14,
  'project-name':           32,
  'project-manager':        22,
  'project-status':         18,
  'project-type':           20,
  'health-indicator':       12,
  'client-code':            14,
  'client-name':            28,
  'client-contact':         22,
  'account-manager':        22,
  budget:                   16,
  'actual-sales':           16,
  'sales-variance':         16,
  'cost-budget':            16,
  'actual-cost':            16,
  'cost-variance':          16,
  'gross-margin':           14,
  'billing-status':         16,
  'billable':               12,
  'wip-amount':             16,
  'gross-profit':           16,
  'forecast-at-completion': 22,
  'team-members':           14,
  'allocated-hours':        18,
  'actual-hours':           14,
  'hours-variance':         14,
  'utilisation-rate':       16,
  phase:                    16,
  'completion-percentage':  16,
  'last-updated':           14,
  'start-date':             12,
  'end-date':               12,
  duration:                 14,
  'days-remaining':         14,
  'next-milestone':         28,
  'milestones-completed':   20,
  'overdue-milestones':     18,
  'tasks-overdue':          14,
  'risk-level':             12,
  'quality-score':          14,
};

// ── Column classification ────────────────────────────────────────────────────

const CURRENCY_IDS = new Set([
  'budget', 'actual-sales', 'sales-variance',
  'cost-budget', 'actual-cost', 'cost-variance',
  'wip-amount', 'gross-profit', 'forecast-at-completion',
]);

const PERCENT_IDS = new Set([
  'gross-margin', 'completion-percentage', 'utilisation-rate',
]);

const RIGHT_ALIGN_IDS = new Set([
  ...CURRENCY_IDS,
  ...PERCENT_IDS,
  'team-members', 'allocated-hours', 'actual-hours',
  'hours-variance', 'duration', 'days-remaining',
  'overdue-milestones', 'tasks-overdue',
]);

// ── Reference data lookups ───────────────────────────────────────────────────

function getClientNames(project: Project, clients: Client[]): string {
  if (project.clientNames) return project.clientNames;
  if (project.clientIds) {
    return project.clientIds
      .split(',')
      .filter(Boolean)
      .map((id) => clients.find((c) => c.id === parseInt(id, 10))?.name || 'Unknown')
      .join(', ');
  }
  return '';
}

function getClientCodes(project: Project, clients: Client[]): string {
  if (!project.clientIds) return '';
  return project.clientIds
    .split(',')
    .filter(Boolean)
    .map((id) => clients.find((c) => c.id === parseInt(id, 10))?.code || '-')
    .join(', ');
}

function getClientContacts(project: Project, clients: Client[]): string {
  if (!project.clientIds) return '';
  return project.clientIds
    .split(',')
    .filter(Boolean)
    .map((id) => clients.find((c) => c.id === parseInt(id, 10))?.contactName)
    .filter(Boolean)
    .join(', ');
}

function getAccountManagerName(project: Project, employees: Employee[]): string {
  if (!project.accountManagerId) return '';
  return getManagerName(project.accountManagerId, employees);
}

function getManagerName(managerId: number, employees: Employee[]): string {
  return employees.find((e) => e.id === managerId)?.name || '';
}

function getOverdueTasks(projectId: number, tasks: Task[]): number {
  const now = new Date();
  return tasks.filter(
    (t) => t.projectId === projectId && t.status !== 'completed' && new Date(t.dueDate) < now,
  ).length;
}

function getOverdueMilestones(projectId: number, milestones: Milestone[]): number {
  return milestones.filter((m) => m.projectId === projectId && m.status === 'overdue').length;
}

function getCompletedMilestones(projectId: number, milestones: Milestone[]): number {
  return milestones.filter((m) => m.projectId === projectId && m.status === 'completed').length;
}

function getTotalMilestones(projectId: number, milestones: Milestone[]): number {
  return milestones.filter((m) => m.projectId === projectId).length;
}

function getAllocatedResources(projectId: number, allocations: ResourceAllocation[]): number {
  const unique = new Set(
    allocations.filter((a) => a.projectId === projectId).map((a) => a.employeeId),
  );
  return unique.size;
}

function getNextMilestoneName(projectId: number, milestones: Milestone[]): string {
  const upcoming = milestones
    .filter((m) => m.projectId === projectId && m.status === 'pending')
    .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
  if (!upcoming[0]) return '';
  return `${upcoming[0].name} (${formatDate(upcoming[0].dueDate)})`;
}

function getUtilisationRate(project: Project): number {
  const { formulaType } = getUtilisationSettings();
  return computeUtilisationRate(project, formulaType);
}

function calculateDuration(startDate: string, endDate: string): number {
  const start = new Date(startDate);
  const end   = new Date(endDate);
  return Math.ceil(Math.abs(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
}

function healthLabel(health: string | undefined): string {
  if (health === 'green') return 'Healthy';
  if (health === 'amber') return 'At Risk';
  if (health === 'red')   return 'Critical';
  return health || '';
}

function deriveProjectPhaseForExport(statusId: number, completionPercent: number): string {
  switch (statusId) {
    case 7: return 'Opportunity';
    case 1: return 'Initiation';
    case 2:
    case 4:
      if (completionPercent <= 25) return 'Planning';
      if (completionPercent <= 75) return 'Execution';
      return 'Closing';
    case 3: return 'On Hold';
    case 5: return 'Cancelled';
    case 6: return 'Closed';
    default: return '';
  }
}

function deriveBillingStatusForExport(project: Project): string {
  if (project.statusId === 5) return 'Cancelled';
  if (project.statusId === 7) return 'Not Started';
  const hasCosts = (project.actualLabour ?? 0) + (project.actualExpense ?? 0) > 0;
  const hasSales = (project.actualSales ?? 0) > 0;
  const unbilled = project.unbilledAmount ?? 0;
  if (!hasCosts && !hasSales) return 'Not Started';
  if (!hasSales && hasCosts) return 'Unbilled';
  if (project.statusId === 6 && unbilled <= 0) return 'Fully Billed';
  if ((project.wipAmount ?? 0) < 0) return 'Over-billed';
  if (hasSales && unbilled > 0) return 'Partially Billed';
  return 'In Progress';
}

function deriveRiskLevelForExport(project: Project): string {
  const thresholds = getBudgetHealthThresholds();
  const breakdown = deriveProjectHealthWithBreakdown(
    {
      actualCost: (project.actualLabour ?? 0) + (project.actualExpense ?? 0),
      budgetCost: (project.budgetLabour ?? 0) + (project.budgetExpense ?? 0),
      startDate: project.startDate,
      endDate: project.endDate,
      completionPercent: project.completionPercent ?? 0,
      actualHours: project.actualHours ?? 0,
      budgetHours: project.budgetHours ?? 0,
      wipAmount: project.wipAmount ?? 0,
      actualRevenue: project.actualRevenue ?? 0,
      budgetRevenue: project.budgetRevenue ?? 0,
      actualGrossMarginPct: null,
      realisationRatePct: null,
      forecastEndDate: project.forecastEndDate,
    },
    thresholds,
  );
  const redCount = breakdown.dimensions.filter(d => d.signal === 'red').length;
  const amberCount = breakdown.dimensions.filter(d => d.signal === 'amber').length;
  if (redCount >= 2) return 'High';
  if (redCount >= 1 || amberCount >= 2) return 'Medium';
  if (amberCount >= 1) return 'Low';
  return 'None';
}

function deriveQualityScoreForExport(project: Project): number | string {
  const thresholds = getBudgetHealthThresholds();
  const breakdown = deriveProjectHealthWithBreakdown(
    {
      actualCost: (project.actualLabour ?? 0) + (project.actualExpense ?? 0),
      budgetCost: (project.budgetLabour ?? 0) + (project.budgetExpense ?? 0),
      startDate: project.startDate,
      endDate: project.endDate,
      completionPercent: project.completionPercent ?? 0,
      actualHours: project.actualHours ?? 0,
      budgetHours: project.budgetHours ?? 0,
      wipAmount: project.wipAmount ?? 0,
      actualRevenue: project.actualRevenue ?? 0,
      budgetRevenue: project.budgetRevenue ?? 0,
      actualGrossMarginPct: null,
      realisationRatePct: null,
      forecastEndDate: project.forecastEndDate,
    },
    thresholds,
  );
  if (breakdown.dimensions.length === 0) return '';
  const points = breakdown.dimensions.reduce((sum, d) => {
    if (d.signal === 'green') return sum + 100;
    if (d.signal === 'amber') return sum + 50;
    return sum;
  }, 0);
  return Math.round(points / breakdown.dimensions.length);
}

// ── Project → cell values ────────────────────────────────────────────────────

/**
 * Resolve the raw cell value for a single project / column.
 * Numbers are kept as numbers so Excel can format and compute them.
 */
function resolveProjectCellValue(
  project: Project,
  colId: string,
  ref: ExportReferenceData,
  statusLabels: Record<number, string>,
  gpMethod: GpCalculationMethod,
): string | number | null {
  switch (colId) {
    case 'project-code':    return project.code || '';
    case 'project-name':    return project.name || '';
    case 'project-manager': return getManagerName(project.projectManagerId, ref.employees);
    case 'project-status': {
      if (project.statusId == null) return '';
      return statusLabels[project.statusId] || PROJECT_STATUS_LABELS[project.statusId] || String(project.statusId);
    }
    case 'project-type':       return project.type ? formatProjectType(project.type) : '';
    case 'health-indicator':   return healthLabel(project.health);
    case 'client-code':        return getClientCodes(project, ref.clients);
    case 'client-name':        return getClientNames(project, ref.clients);
    case 'client-contact':     return getClientContacts(project, ref.clients);
    case 'account-manager':    return getAccountManagerName(project, ref.employees);

    // Financial — numeric so Excel can sum / format
    case 'budget':          return project.budgetRevenue ?? 0;
    case 'actual-sales':    return project.actualRevenue ?? 0;
    case 'sales-variance':  return project.salesVariance ?? 0;
    case 'cost-budget':     return (project.budgetLabour ?? 0) + (project.budgetExpense ?? 0);
    case 'actual-cost':     return (project.actualLabour ?? 0) + (project.actualExpense ?? 0);
    case 'cost-variance':   return project.costVariance ?? 0;
    case 'gross-margin': {
      const result = calculateGP(
        {
          revenue:              project.actualSales ?? 0,
          totalDirectCosts:     project.actualCosts ?? 0,
          labourCosts:          project.actualLabourCosts ?? 0,
          subcontractorCosts:   project.actualSubcontractorCosts ?? 0,
        },
        gpMethod,
      );
      return result.grossMarginPct;
    }
    case 'wip-amount':     return project.wipAmount ?? 0;
    case 'gross-profit':   return (project.actualSales ?? 0) - (project.actualCosts ?? 0);
    case 'forecast-at-completion': {
      const totalBudget = (project.budgetLabour ?? 0) + (project.budgetExpense ?? 0);
      const completionPct = project.completionPercent ?? 0;
      if (completionPct === 0) return totalBudget;
      return calculateEAC(
        { actualCosts: project.actualCosts ?? 0, totalBudget, completionPercent: completionPct },
        'simple',
      ).eac;
    }

    // Resource
    case 'team-members':      return getAllocatedResources(project.id, ref.resourceAllocations);
    case 'allocated-hours':   return project.budgetHours ?? 0;
    case 'actual-hours':      return project.actualHours ?? 0;
    case 'hours-variance':    return project.hoursVariance ?? 0;
    case 'utilisation-rate':  return Math.round(getUtilisationRate(project) * 10) / 10;

    // Status / timeline
    case 'phase':                 return deriveProjectPhaseForExport(project.statusId, project.completionPercent ?? 0);
    case 'completion-percentage': return computeEffectiveCompletionPercent(project);
    case 'last-updated':          return formatDate(project.updatedAt);
    case 'start-date':            return formatDate(project.startDate);
    case 'end-date':              return formatDate(project.endDate);
    case 'duration':
      return project.startDate && project.endDate
        ? calculateDuration(project.startDate, project.endDate)
        : 0;
    case 'days-remaining': {
      const days = calculateDaysRemaining(project.endDate);
      return days ?? '';
    }
    case 'next-milestone':   return getNextMilestoneName(project.id, ref.milestones);

    // Performance
    case 'milestones-completed': {
      const completed = getCompletedMilestones(project.id, ref.milestones);
      const total     = getTotalMilestones(project.id, ref.milestones);
      return total > 0 ? `${completed}/${total}` : '';
    }
    case 'overdue-milestones': return getOverdueMilestones(project.id, ref.milestones);
    case 'tasks-overdue':      return getOverdueTasks(project.id, ref.tasks);
    case 'billing-status':     return deriveBillingStatusForExport(project);
    case 'billable':           return (project.billable ?? true) ? 'Yes' : 'No';
    case 'risk-level':         return deriveRiskLevelForExport(project);
    case 'quality-score':      return deriveQualityScoreForExport(project);

    default: return '';
  }
}

// ── Subtotals → numeric cell value for a column ──────────────────────────────

/**
 * Map a column id to the corresponding subtotal field.
 * Returns undefined for columns that don't have a meaningful group subtotal.
 */
function subtotalValue(colId: string, sub: GroupSubtotals): number | undefined {
  switch (colId) {
    case 'budget':            return sub.budgetRevenue;
    case 'actual-sales':      return sub.actualRevenue;
    case 'cost-budget':       return sub.costBudget;
    case 'actual-cost':       return sub.actualCost;
    case 'gross-profit':      return sub.grossProfit;
    case 'wip-amount':        return sub.wipAmount;
    case 'allocated-hours':   return sub.budgetHours;
    case 'actual-hours':      return sub.actualHours;
    default:                  return undefined;
  }
}

// ── Row styling helpers ──────────────────────────────────────────────────────

function applyNumberFormat(cell: ExcelJS.Cell, colId: string): void {
  if (CURRENCY_IDS.has(colId)) {
    cell.numFmt = '£#,##0.00';
    cell.alignment = { horizontal: 'right', vertical: 'middle' };
  } else if (PERCENT_IDS.has(colId)) {
    cell.numFmt = '0.0"%"';
    cell.alignment = { horizontal: 'right', vertical: 'middle' };
  } else if (RIGHT_ALIGN_IDS.has(colId)) {
    cell.alignment = { horizontal: 'right', vertical: 'middle' };
  }
}

function applyDataRowStyle(
  row: ExcelJS.Row,
  visibleCols: ColumnDefinition[],
  isAlt: boolean,
): void {
  if (isAlt) {
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_BG } } as Fill;
    });
  }
  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const colDef = visibleCols[colNumber - 1];
    if (!colDef) return;
    applyNumberFormat(cell, colDef.id);
  });
  row.height = 18;
}

// ── Sheet writers ────────────────────────────────────────────────────────────

/** Write a styled column-header row (frozen, dark navy). */
function writeHeaderRow(
  sheet: ExcelJS.Worksheet,
  visibleCols: ColumnDefinition[],
): void {
  const headers = visibleCols.map((c) => HEADER_LABELS[c.id] ?? c.name);
  const headerRow = sheet.addRow(headers);

  headerRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_BLUE } } as Fill;
    cell.font = { bold: true, color: { argb: HEADER_TEXT }, size: 11 };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: false };
    cell.border = { bottom: { style: 'thin', color: { argb: HEADER_TEXT } } };
  });
  headerRow.height = 22;
}

/** Write a single project data row. */
function writeProjectRow(
  sheet: ExcelJS.Worksheet,
  project: Project,
  visibleCols: ColumnDefinition[],
  ref: ExportReferenceData,
  statusLabels: Record<number, string>,
  gpMethod: GpCalculationMethod,
  isAlt: boolean,
): void {
  const values = visibleCols.map((col) =>
    resolveProjectCellValue(project, col.id, ref, statusLabels, gpMethod),
  );
  const row = sheet.addRow(values);
  applyDataRowStyle(row, visibleCols, isAlt);
}

/**
 * Write a group header band row.
 * The label spans all columns and is styled according to the group's depth.
 * An indent prefix (·· per level) makes nesting visually clear.
 */
function writeGroupHeaderRow(
  sheet: ExcelJS.Worksheet,
  label: string,
  count: number,
  depth: number,
  totalCols: number,
): void {
  const indent  = '  '.repeat(depth);
  const bgArgb  = GROUP_HEADER_BG[Math.min(depth, GROUP_HEADER_BG.length - 1)];
  const display = `${indent}${label}  (${count} project${count !== 1 ? 's' : ''})`;

  // Build a row with the label in column 1, empty in the rest
  const values: (string | null)[] = [display, ...Array(totalCols - 1).fill(null)];
  const row = sheet.addRow(values);

  row.eachCell({ includeEmpty: true }, (cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgArgb } } as Fill;
    cell.font = { bold: true, color: { argb: GROUP_HEADER_TEXT }, size: 10 };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
  });
  row.height = 20;

  // Merge cells across the whole row so the label reads as a single band
  if (totalCols > 1) {
    sheet.mergeCells(row.number, 1, row.number, totalCols);
  }
}

/**
 * Write a subtotal row beneath a group's data rows.
 * Only columns that have a meaningful sum (currency / hour fields) receive a
 * value — everything else is left blank.
 */
function writeSubtotalRow(
  sheet: ExcelJS.Worksheet,
  subtotals: GroupSubtotals,
  label: string,
  depth: number,
  visibleCols: ColumnDefinition[],
): void {
  const bgArgb = SUBTOTAL_BG[Math.min(depth, SUBTOTAL_BG.length - 1)];

  const values: (string | number | null)[] = visibleCols.map((col, idx) => {
    if (idx === 0) return label; // first cell = "Subtotal: <group>"
    const sv = subtotalValue(col.id, subtotals);
    return sv !== undefined ? sv : null;
  });

  const row = sheet.addRow(values);

  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgArgb } } as Fill;
    cell.font = { bold: true, size: 10, italic: colNumber === 1 };
    cell.alignment = { vertical: 'middle', horizontal: colNumber === 1 ? 'left' : 'right' };

    // Apply currency/percent formats to subtotal numbers
    const colDef = visibleCols[colNumber - 1];
    if (colDef) {
      if (CURRENCY_IDS.has(colDef.id)) cell.numFmt = '£#,##0.00';
      else if (PERCENT_IDS.has(colDef.id)) cell.numFmt = '0.0"%"';
    }
  });

  row.height = 18;
}

/**
 * Recursively write all groups (and their sub-groups) into the sheet.
 *
 * For each group:
 *   1. Group header band
 *   2.a If no sub-groups → project data rows
 *   2.b If sub-groups → recurse for each sub-group
 *   3. Subtotal row
 *   4. Empty spacer row between top-level groups for readability
 */
function writeGroups(
  sheet: ExcelJS.Worksheet,
  groups: ProjectGroup[],
  visibleCols: ColumnDefinition[],
  ref: ExportReferenceData,
  statusLabels: Record<number, string>,
  gpMethod: GpCalculationMethod,
  depth: number,
  dataRowCounter: { count: number }, // shared mutable ref for alt-row zebra striping
): void {
  for (const group of groups) {
    // ── Group header ──────────────────────────────────────────────────────
    writeGroupHeaderRow(sheet, group.label, group.projects.length, depth, visibleCols.length);

    if (group.subGroups && group.subGroups.length > 0) {
      // ── Nested sub-groups ─────────────────────────────────────────────
      writeGroups(
        sheet,
        group.subGroups,
        visibleCols,
        ref,
        statusLabels,
        gpMethod,
        depth + 1,
        dataRowCounter,
      );
    } else {
      // ── Leaf project rows ─────────────────────────────────────────────
      for (const project of group.projects) {
        writeProjectRow(
          sheet,
          project,
          visibleCols,
          ref,
          statusLabels,
          gpMethod,
          dataRowCounter.count % 2 === 1,
        );
        dataRowCounter.count++;
      }
    }

    // ── Subtotal row ──────────────────────────────────────────────────────
    const indent = '  '.repeat(depth);
    writeSubtotalRow(sheet, group.subtotals, `${indent}Subtotal: ${group.label}`, depth, visibleCols);

    // Blank spacer between top-level groups only
    if (depth === 0) {
      sheet.addRow([]);
    }
  }
}

// ── Main export function ─────────────────────────────────────────────────────

/**
 * Export the currently-visible project list to an Excel (.xlsx) file.
 *
 * @param projects      The (already filtered + sorted) flat project rows.
 *                      Used when no grouping is active.
 * @param columnDefs    The user's current column definitions (visibility + order).
 * @param ref           Reference data for lookups (clients, employees, etc.)
 * @param statusLabels  Dynamic status label map from ProjectStatusContext.
 * @param groupedData   Optional — pass the `groupedProjects` computed value from
 *                      projects.tsx when grouping is active. When provided the
 *                      sheet is rendered in grouped layout with subtotal rows.
 */

// ── Fetch all project pages ──────────────────────────────────────────────────

const MAX_PROJECT_PAGES = 200;
const MAX_PROJECT_ROWS  = 50_000;

/**
 * Loops through all pages of the projects endpoint via offset-based
 * pagination. Stops when there are no more pages or the safety limit
 * is reached.
 */
export async function fetchAllProjectPages(
  featureId: string,
  filters?: ProjectFilterParams,
): Promise<Project[]> {
  const allProjects: Project[] = [];
  const pageSize = 500;
  let page = 1;

  while (page <= MAX_PROJECT_PAGES && allProjects.length < MAX_PROJECT_ROWS) {
    const result = await fetchProjects(featureId, { page, pageSize }, filters);

    let data: Project[];
    let pagination: PaginationMetadata | undefined;

    if (result && typeof result === 'object' && 'data' in result) {
      data = result.data as Project[];
      pagination = result.pagination as PaginationMetadata | undefined;
    } else if (Array.isArray(result)) {
      data = result as Project[];
    } else {
      break;
    }

    if (data.length === 0) break;
    allProjects.push(...data);

    if (!pagination?.hasNextPage) break;
    page++;
  }

  return allProjects;
}

export async function exportProjectsToExcel(
  projects: Project[],
  columnDefs: ColumnDefinition[],
  ref: ExportReferenceData,
  statusLabels: Record<number, string>,
  groupedData?: ProjectGroup[],
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Access Project Management';
  workbook.created = new Date();

  const isGrouped = groupedData && groupedData.length > 0;

  const sheet = workbook.addWorksheet('Projects', {
    views: [{ state: 'frozen', ySplit: 1 }], // Freeze column header row
    pageSetup: {
      orientation: 'landscape',
      fitToPage: true,
      fitToWidth: 1,
    },
  });

  // Visible columns in display order
  const visibleCols = columnDefs
    .filter((c) => c.visible)
    .sort((a, b) => a.order - b.order);

  // Set column widths
  sheet.columns = visibleCols.map((col, idx) => ({
    key: String(idx),
    width: COLUMN_WIDTHS[col.id] ?? 18,
  }));

  const gpMethod = getFinanceSettings().gpCalculationMethod as GpCalculationMethod;

  // ── Column header row (always present) ─────────────────────────────────────
  writeHeaderRow(sheet, visibleCols);

  // ── Data rows ───────────────────────────────────────────────────────────────

  if (isGrouped) {
    // Grouped layout — mirrors the on-screen grouped view
    const dataRowCounter = { count: 0 };
    writeGroups(sheet, groupedData, visibleCols, ref, statusLabels, gpMethod, 0, dataRowCounter);
  } else {
    // Flat layout — one row per project
    projects.forEach((project, rowIdx) => {
      writeProjectRow(sheet, project, visibleCols, ref, statusLabels, gpMethod, rowIdx % 2 === 1);
    });
  }

  // ── Metadata sheet ──────────────────────────────────────────────────────────

  const meta = workbook.addWorksheet('Export Info');
  meta.addRow(['Field', 'Value']);
  meta.addRow(['Exported On',       new Date().toLocaleString('en-GB')]);
  meta.addRow(['Total Projects',    projects.length]);
  meta.addRow(['Columns Exported',  visibleCols.length]);
  meta.addRow(['GP Method',         gpMethod]);
  meta.addRow(['Grouped',           isGrouped ? 'Yes' : 'No']);
  if (isGrouped && groupedData) {
    meta.addRow(['Group Count', groupedData.length]);
  }
  meta.getRow(1).font = { bold: true };
  meta.columns = [{ width: 22 }, { width: 40 }];

  // ── Trigger browser download ────────────────────────────────────────────────

  const today    = new Date().toISOString().slice(0, 10);
  const filename = `Projects_Export_${today}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob   = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
